import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { migrosLogoDataUrl } from './migros-logo-data';
import type { LineReportPDFData } from './LineInspectionReportPDF';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const NILL = 'Nill';

const formatDate = (raw?: string | null) => {
  if (!raw) return NILL;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.');
};

const val = (v: any): string => {
  if (v === null || v === undefined) return NILL;
  const str = String(v).trim();
  return str.length === 0 ? NILL : str;
};

const BORDER = '#000000';

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#000000',
    backgroundColor: '#ffffff',
    paddingHorizontal: 32,
    paddingTop: 18,
    paddingBottom: 50,
  },

  topHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  logoImg: { width: 140, height: 26, objectFit: 'contain' },
  subTitle: { fontSize: 8.5, color: '#000000', marginTop: 6, marginBottom: 10 },
  bigSectionTitle: { fontSize: 22, fontFamily: 'Helvetica-Bold', color: '#000000', marginVertical: 12, marginLeft: 18 },

  stageRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },

  infoRow: { flexDirection: 'row', borderTopWidth: 1, borderTopColor: BORDER, minHeight: 20 },
  infoLabelCell: { width: 180, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  infoLabelText: { fontSize: 9 },
  infoValueCell: { flex: 1, padding: 4, paddingHorizontal: 6, justifyContent: 'center' },
  infoValueText: { fontSize: 9, fontFamily: 'Helvetica-Bold' },

  resultHeaderRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, marginTop: 14 },
  resultHeaderLabel: { flex: 1, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  resultHeaderLabelText: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
  resultHeaderCheckCell: { flex: 1, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  resultHeaderCheckCellLast: { flex: 1, padding: 4, paddingHorizontal: 6, justifyContent: 'center' },

  sigRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, marginTop: 14 },
  sigCell: { flex: 1, padding: 6, borderRightWidth: 1, borderRightColor: BORDER, minHeight: 70 },
  sigCellLast: { flex: 1, padding: 6, minHeight: 70 },
  sigTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  sigField: { fontSize: 8.5, marginBottom: 2 },
  sigImage: { width: 90, height: 32, objectFit: 'contain', marginTop: 2 },

  sectionTitle: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginTop: 12, marginBottom: 6, textDecoration: 'underline' },

  dataTable: { borderWidth: 1, borderColor: BORDER, marginBottom: 6 },
  dataHeadRow: { flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 1, borderBottomColor: BORDER },
  dataBodyRow: { flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: BORDER, minHeight: 20 },
  dataCell: { flex: 1, padding: 4, paddingHorizontal: 5, borderRightWidth: 0.5, borderRightColor: BORDER, justifyContent: 'center' },
  dataCellLast: { flex: 1, padding: 4, paddingHorizontal: 5, justifyContent: 'center' },
  dataCellText: { fontSize: 8.5 },
  dataCellTextBold: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  dataCellTextCenter: { fontSize: 8.5, textAlign: 'center' },
  dataCellTextHead: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', textAlign: 'center' },

  checkboxBoxLg: { width: 11, height: 11, borderWidth: 0.7, borderColor: BORDER, alignItems: 'center', justifyContent: 'center' },
  checkboxMarkLg: { fontSize: 9, fontFamily: 'Helvetica-Bold', lineHeight: 1 },
  checkboxLabel: { fontSize: 9, marginLeft: 4 },
  checkboxItem: { flexDirection: 'row', alignItems: 'center', marginRight: 8 },

  remarksBlock: { borderWidth: 1, borderColor: BORDER, padding: 6, minHeight: 26, marginBottom: 6 },
  remarksLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 4 },

  photoGrid: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
  photoTile: { width: 162 },
  photoImg: { width: 162, height: 160, objectFit: 'cover' },
  photoCaption: { fontSize: 7.5, color: '#444', marginTop: 2 },

  pageFooter: {
    position: 'absolute', bottom: 14, left: 32, right: 32,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
  },
  footerLeft: { fontSize: 7.5, color: '#555555', flex: 1 },
  footerCenter: { fontSize: 7.5, color: '#555555', textAlign: 'center', flex: 1 },
  footerRight: { fontSize: 7.5, color: '#555555', textAlign: 'right', flex: 1 },
});

// ─── Sub-components ───────────────────────────────────────────────────────────
const PageTop = ({ logoSrc }: { logoSrc: string }) => (
  <>
    <View style={s.topHeader} fixed>
      <Image src={logoSrc} style={s.logoImg} />
    </View>
    <Text style={s.subTitle} fixed>FINALRANDOMINSPECTION(TEXTILE/HOMETEXTILE)Version-1</Text>
  </>
);

const PageFooter = () => (
  <View style={s.pageFooter} fixed>
    <Text style={s.footerLeft}>MIGROS Asia</Text>
    <Text style={s.footerCenter} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
    <Text style={s.footerRight}>Line Inspection</Text>
  </View>
);

const Checkbox = ({ checked, label }: { checked?: boolean; label: string }) => (
  <View style={s.checkboxItem}>
    <View style={s.checkboxBoxLg}>
      {checked ? <Text style={s.checkboxMarkLg}>X</Text> : null}
    </View>
    <Text style={s.checkboxLabel}>{label}</Text>
  </View>
);

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <View style={s.infoRow}>
    <View style={s.infoLabelCell}><Text style={s.infoLabelText}>{label}</Text></View>
    <View style={s.infoValueCell}><Text style={s.infoValueText}>{value}</Text></View>
  </View>
);

// ─── Main PDF Document ────────────────────────────────────────────────────────
const MigrosLineInspectionReportPDF = ({ inspection, logoDataUrl }: { inspection: LineReportPDFData; logoDataUrl?: string }) => {
  const logoSrc = logoDataUrl || migrosLogoDataUrl;
  const result = (inspection.result || 'PENDING').toUpperCase();
  const isPass = result === 'PASS';
  const isFail = result === 'FAIL';

  const checklists = inspection.checklists || [];
  const defects = inspection.defects || [];
  const verifications = inspection.verifications || [];
  const photos = inspection.photos || [];

  const passCount = checklists.filter(c => (c.status || '').toUpperCase() === 'PASS').length;
  const totalDefects = defects.reduce((acc, d) => acc + (d.count || 0), 0);

  // Collect all photos for pictures pages
  const allPhotos: { url: string; label?: string }[] = [];
  for (const p of photos) {
    if (p.image_url) allPhotos.push({ url: p.image_url, label: p.label || p.caption || '' });
  }
  for (const item of checklists) {
    for (const url of item.evidence_images || []) {
      if (url) allPhotos.push({ url, label: `${item.title || item.category || 'Checklist'} Evidence` });
    }
  }
  for (const d of defects) {
    for (const url of d.evidence_images || []) {
      if (url) allPhotos.push({ url, label: `Defect: ${d.title || d.type || ''}` });
    }
  }

  return (
    <Document title={`Migros Line Inspection Report - ${inspection.id.slice(0, 8).toUpperCase()}`}>

      {/* ═══ PAGE 1 — COVER ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        <View style={s.stageRow}>
          <Checkbox label="Factory Self Inspection" />
          <Checkbox label="Line Inspection" checked />
          <Checkbox label="Re-Inspection" />
        </View>

        <View style={{ borderWidth: 1, borderColor: BORDER }}>
          <InfoRow label="Inspection Date:" value={formatDate(inspection.created_at)} />
          <InfoRow label="PO / Batch / Shift:" value={val(inspection.basis)} />
          <InfoRow label="Ref Number:" value={val(inspection.ref_number)} />
          <InfoRow label="Production Process:" value={val(inspection.process?.name)} />
          <InfoRow label="Fabric Type:" value={val(inspection.fabric_type_name || inspection.fabric_type)} />
          <InfoRow label="Sampling Method:" value={val(inspection.sampling_method)} />
          <InfoRow label="Lot Size:" value={inspection.lot_size != null ? String(inspection.lot_size) : NILL} />
          <InfoRow label="Sample Size:" value={inspection.sample_size != null ? String(inspection.sample_size) : NILL} />
          <InfoRow label="Inspector Name:" value={val(inspection.inspector_name)} />
          <InfoRow label="Status:" value={val(inspection.status)} />
        </View>

        <View style={s.resultHeaderRow}>
          <View style={s.resultHeaderLabel}>
            <Text style={s.resultHeaderLabelText}>INSPECTIONRESULT</Text>
          </View>
          <View style={s.resultHeaderCheckCell}>
            <Checkbox checked={isPass} label="PASSED" />
          </View>
          <View style={s.resultHeaderCheckCellLast}>
            <Checkbox checked={isFail} label="FAILED" />
          </View>
        </View>

        <View style={s.sigRow}>
          <View style={s.sigCell}>
            <Text style={s.sigTitle}>INSPECTOR</Text>
            <Text style={s.sigField}>Name: {val(inspection.inspector_name)}</Text>
            <Text style={s.sigField}>Signature:</Text>
            {inspection.inspector_signature_url ? (
              <Image src={inspection.inspector_signature_url} style={s.sigImage} />
            ) : null}
          </View>
          <View style={s.sigCellLast}>
            <Text style={s.sigTitle}>FACTORY REPRESENTATIVE</Text>
            <Text style={s.sigField}>Name:</Text>
            <Text style={s.sigField}>Signature:</Text>
            {inspection.factory_rep_signature_url ? (
              <Image src={inspection.factory_rep_signature_url} style={s.sigImage} />
            ) : null}
          </View>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 2 — Checklist + Defects + Verifications ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        <Text style={s.sectionTitle}>Part1- Checklist Results ({passCount}/{checklists.length} Passed)</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={[s.dataCell, { flex: 2 }]}><Text style={s.dataCellTextHead}>Item</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Status</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Remarks</Text></View>
          </View>
          {checklists.length > 0 ? (
            checklists.map((item, i) => (
              <View key={i} style={s.dataBodyRow}>
                <View style={[s.dataCell, { flex: 2 }]}><Text style={s.dataCellText}>{item.title || item.category || '—'}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{(item.status || '').toUpperCase() || NILL}</Text></View>
                <View style={s.dataCellLast}><Text style={s.dataCellText}>{val(item.remarks)}</Text></View>
              </View>
            ))
          ) : (
            <View style={s.dataBodyRow}>
              <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            </View>
          )}
        </View>

        {defects.length > 0 && (
          <>
            <Text style={s.sectionTitle}>Part2- Defect Log (Total Count: {totalDefects})</Text>
            <View style={s.dataTable}>
              <View style={s.dataHeadRow}>
                <View style={s.dataCell}><Text style={s.dataCellTextHead}>Title</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextHead}>Category</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextHead}>Type</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextHead}>Count</Text></View>
                <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Severity</Text></View>
              </View>
              {defects.map((d, i) => (
                <View key={i} style={s.dataBodyRow}>
                  <View style={s.dataCell}><Text style={s.dataCellText}>{val(d.title)}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellText}>{val(d.category)}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellText}>{val(d.type)}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(d.count)}</Text></View>
                  <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{val(d.severity)}</Text></View>
                </View>
              ))}
            </View>
          </>
        )}

        {verifications.length > 0 && (
          <>
            <Text style={s.sectionTitle}>Part3- Measurements & Verifications</Text>
            <View style={s.dataTable}>
              <View style={s.dataHeadRow}>
                <View style={[s.dataCell, { flex: 2 }]}><Text style={s.dataCellTextHead}>Measurement Item</Text></View>
                <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Value</Text></View>
              </View>
              {verifications.map((v, i) => {
                const name = v.name || v.title || '—';
                const unit = v.unit || v.units || v.measurement_unit || '';
                const displayVal = v.value ? (unit ? `${v.value} ${unit}` : v.value) : NILL;
                return (
                  <View key={i} style={s.dataBodyRow}>
                    <View style={[s.dataCell, { flex: 2 }]}><Text style={s.dataCellText}>{name}</Text></View>
                    <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{displayVal}</Text></View>
                  </View>
                );
              })}
            </View>
          </>
        )}

        {inspection.remarks && (
          <>
            <Text style={s.sectionTitle}>Remarks</Text>
            <View style={s.remarksBlock}>
              <Text style={{ fontSize: 9 }}>{val(inspection.remarks)}</Text>
            </View>
          </>
        )}

        <PageFooter />
      </Page>

      {/* ═══ PAGE 3+ — PICTURES ═══ */}
      {allPhotos.length === 0 ? (
        <Page size="A4" style={s.page}>
          <PageTop logoSrc={logoSrc} />
          <Text style={s.bigSectionTitle}>PICTURES</Text>
          <Text style={{ fontSize: 9, marginLeft: 18, color: '#444' }}>{NILL}</Text>
          <PageFooter />
        </Page>
      ) : (
        (() => {
          const pages: { url: string; label?: string }[][] = [];
          for (let i = 0; i < allPhotos.length; i += 6) pages.push(allPhotos.slice(i, i + 6));
          return pages.map((chunk, pi) => (
            <Page key={pi} size="A4" style={s.page}>
              <PageTop logoSrc={logoSrc} />
              {pi === 0 ? <Text style={s.bigSectionTitle}>PICTURES</Text> : null}
              <View style={s.photoGrid}>
                {chunk.map((ph, i) => (
                  <View key={i} style={s.photoTile}>
                    <Image src={ph.url} style={s.photoImg} />
                    {ph.label ? <Text style={s.photoCaption}>{ph.label}</Text> : null}
                  </View>
                ))}
              </View>
              <PageFooter />
            </Page>
          ));
        })()
      )}

    </Document>
  );
};

export default MigrosLineInspectionReportPDF;
